import { StateMachine } from "@/core/arch/state_machine";
import type { JobRecord, JobStatus } from "./types";

// ── State ─────────────────────────────────────────────────────────────────────

export type JobPhase =
  | { kind: "idle" }
  | { kind: "kicking_off" }
  | { kind: "watching"; job: JobRecord }
  | { kind: "cancelling"; job: JobRecord }
  | { kind: "error"; message: string };

export interface JobBlocState {
  phase: JobPhase;
  /** 1-based position in a chain of successor jobs the BLoC has followed. */
  link: number;
}

// ── Repository interface ──────────────────────────────────────────────────────

/** Optional per-tool behaviours the generic BLoC delegates to. */
export interface JobBlocOptions {
  /**
   * Given a job that has just reached a terminal status, returns the id of a
   * successor to follow, or null to stop watching.
   *
   * A job whose work does not fit one Lambda budget finishes cleanly and enqueues
   * a continuation. Following it keeps the panel showing one logical run rather
   * than appearing to finish part-way through.
   */
  followChain?: (job: JobRecord) => string | null;
}

export interface JobRepository {
  kickoff(input?: Record<string, unknown>): Promise<string>;
  findRunningJob(): Promise<JobRecord | null>;
  /** Current state of one job, or null if it is not readable yet. */
  getJob(jobId: string): Promise<JobRecord | null>;
  cancelJob(jobId: string): Promise<void>;
  subscribeToJob(
    jobId: string,
    onUpdate: (job: JobRecord) => void,
    onError: (err: Error) => void,
  ): () => void;
}

// ── BLoC ─────────────────────────────────────────────────────────────────────

/**
 * Owns the lifecycle of a single background Job: kickoff, attach to the running
 * record, surface progress, cooperative cancel, terminal display.
 *
 * Job-type agnostic — the type only shapes the optimistic seed record, since
 * everything after kickoff comes from the live subscription. Tools that need no
 * bespoke kickoff form should use this rather than adding another per-tool BLoC.
 */
export class JobBloc extends StateMachine<JobBlocState> {
  private _unsubscribe: (() => void) | null = null;
  private disposed = false;
  /**
   * Identifies the current attachment. `unsubscribe` cannot recall a callback
   * already in flight, so every callback carries the token it was created with
   * and anything from a superseded attachment is dropped.
   */
  private attachment = 0;
  /** Whether the live attachment's subscription has delivered anything yet. */
  private observed = false;

  constructor(
    private readonly repo: JobRepository,
    private readonly jobType: string,
    private readonly options: JobBlocOptions = {},
  ) {
    super({ phase: { kind: "idle" }, link: 1 });
    this.appendDisposeCallback(this.teardown);
  }

  private readonly teardown = (): void => {
    this.disposed = true;
    this._detach();
  };

  /**
   * Returns a disposed bloc to service, matching JobDetailBloc and
   * JobHistoryBloc.
   *
   * `useStateMachine` disposes on effect cleanup but keeps the same instance in
   * its ref, so React StrictMode's mount/cleanup/mount replay hands the view a
   * bloc that has already been torn down. Without this every job tool enqueued
   * work that ran to completion while the panel sat on "Starting…" — `_attach`
   * silently no-ops once disposed. Re-registering matters as much as clearing
   * the flag: `dispose` consumes its callbacks, so without it a later real
   * unmount would no longer close the subscription.
   */
  private rearm(): void {
    this.disposed = false;
    this.appendDisposeCallback(this.teardown);
  }

  async resumeIfRunning(): Promise<void> {
    if (this.disposed) this.rearm();
    try {
      const job = await this.repo.findRunningJob();
      if (job) this._attach(job);
    } catch {
      // Non-fatal — user sees idle state and can kick off manually.
    }
  }

  async kickoff(input?: Record<string, unknown>): Promise<void> {
    if (this.disposed) this.rearm();
    this.setState({ phase: { kind: "kicking_off" }, link: 1 });
    try {
      const jobId = await this.repo.kickoff(input);
      this._attach(this._seed(jobId));
    } catch (err) {
      this._fail(err);
    }
  }

  async cancel(): Promise<void> {
    const phase = this.state.phase;
    if (phase.kind !== "watching") return;
    this._setPhase({ kind: "cancelling", job: phase.job });
    try {
      await this.repo.cancelJob(phase.job.id);
    } catch (err) {
      this._fail(err);
    }
  }

  reset(): void {
    this._detach();
    this.setState({ phase: { kind: "idle" }, link: 1 });
  }

  // ── Private ──────────────────────────────────────────────────────────────────

  private _seed(jobId: string, carry?: JobRecord): JobRecord {
    return {
      id: jobId,
      type: this.jobType,
      status: "pending",
      triggeredBy: "",
      total: null,
      // Carried from the predecessor so a chain hand-off does not blank the
      // panel until the successor writes its first progress flush.
      processed: carry?.processed ?? 0,
      counters: carry?.counters ?? null,
      result: null,
      error: null,
      startedAt: null,
      completedAt: null,
    };
  }

  private _attach(seed: JobRecord): void {
    // A kickoff or lookup awaited across an unmount would otherwise open a
    // subscription nothing will ever close.
    if (this.disposed) return;
    this._detach();
    const attachment = ++this.attachment;
    this.observed = false;
    this._setPhase({ kind: "watching", job: seed });

    // `subscribeToJob` is a change feed, so a job that already reached its
    // terminal status emits nothing more and the seed would stand forever.
    // Short runs — a scope of a few merchants — finish inside the kickoff round
    // trip often enough that this is the common case, not an edge one.
    void this.repo
      .getJob(seed.id)
      .then((current) => {
        if (this.disposed || !current) return;
        if (attachment !== this.attachment) return;
        // The subscription is authoritative once it has delivered anything: a
        // read issued before the job started can resolve after `complete` and
        // would otherwise rewind the phase to pending, hanging the panel again.
        if (this.observed) return;
        if (this.state.phase.kind !== "watching") return;
        if (this.state.phase.job.id !== current.id) return;
        this._onJobUpdate(current);
      })
      .catch(() => {
        /* the subscription is still the primary path */
      });

    this._unsubscribe = this.repo.subscribeToJob(
      seed.id,
      (job) => {
        if (attachment !== this.attachment) return;
        this.observed = true;
        this._onJobUpdate(job);
      },
      (err) => {
        if (attachment !== this.attachment) return;
        this._setPhase({ kind: "error", message: err.message });
      },
    );
  }

  /**
   * Folds one observed job record into the phase.
   *
   * Shared by the subscription and the read-on-attach, so a job first seen in a
   * terminal state follows exactly the same path — chain hand-off included — as
   * one that got there while being watched.
   */
  private _onJobUpdate(job: JobRecord): void {
    const isTerminal =
      job.status === "complete" ||
      job.status === "failed" ||
      job.status === "cancelled";
    // Hold the cancelling label until the runner actually stops, so the button
    // state can't flicker back to a cancellable one.
    if (this.state.phase.kind === "cancelling" && !isTerminal) {
      this._setPhase({ kind: "cancelling", job });
      return;
    }
    if (isTerminal) {
      const successorId = this.options.followChain?.(job) ?? null;
      if (successorId && successorId !== job.id) {
        // Bumped silently: _attach emits, so notifying here would flash the
        // predecessor's terminal status before the successor lands.
        this.setState(
          { phase: this.state.phase, link: this.state.link + 1 },
          false,
        );
        this._attach(this._seed(successorId, job));
        return;
      }
    }
    this._setPhase({ kind: "watching", job });
  }

  private _setPhase(phase: JobPhase): void {
    this.setState({ phase, link: this.state.link });
  }

  private _fail(err: unknown): void {
    const message = err instanceof Error ? err.message : String(err);
    this._setPhase({ kind: "error", message });
  }

  private _detach(): void {
    if (this._unsubscribe) {
      this._unsubscribe();
      this._unsubscribe = null;
    }
  }

  get jobStatus(): JobStatus | null {
    const { phase } = this.state;
    if (phase.kind === "watching" || phase.kind === "cancelling") {
      return phase.job.status;
    }
    return null;
  }
}
